// Lógica pura del borrador de la vista "Por club" (sin React ni red), para
// poder testearla sin montar nada. Una fila es
// { jugadorId, nombre, genero, clubId, participantes? }. `guardados` es lo
// que hay en la base de datos; `borrador`, lo que se está editando.
//
// Reglas por estado de la temporada (las mismas que aplica el backend, que
// es la barrera real):
//   PLANIFICADA: todo.
//   INICIADA:    solo altas; una relación ya guardada no se cambia ni se quita.
//   FINALIZADA:  nada.

const ids = (filas) => new Set(filas.map((f) => f.jugadorId));

// Una fila guardada es la que ya existe en la base de datos.
export const esGuardada = (guardados, jugadorId) => ids(guardados).has(jugadorId);

export const puedeAnadir = (estado) => estado === 'PLANIFICADA' || estado === 'INICIADA';

export const puedeReplicar = (estado) => estado === 'PLANIFICADA';

// Mover o quitar: libre en PLANIFICADA; en INICIADA solo las filas nuevas del
// borrador (aún no guardadas); nunca en FINALIZADA.
export const puedeModificar = (estado, guardados, jugadorId) => {
  if (estado === 'PLANIFICADA') return true;
  if (estado === 'INICIADA') return !esGuardada(guardados, jugadorId);
  return false;
};

export function calcularCambios(guardados, borrador) {
  const clubGuardado = new Map(guardados.map((f) => [f.jugadorId, f.clubId]));
  const enBorrador = ids(borrador);
  const altas = [];
  const cambios = [];
  borrador.forEach((f) => {
    const anterior = clubGuardado.get(f.jugadorId);
    if (anterior === undefined) altas.push(f);
    else if (anterior !== f.clubId) cambios.push({ ...f, clubAnteriorId: anterior });
  });
  const bajas = guardados.filter((f) => !enBorrador.has(f.jugadorId));
  return { altas, cambios, bajas };
}

export const hayCambios = (guardados, borrador) => {
  const { altas, cambios, bajas } = calcularCambios(guardados, borrador);
  return altas.length + cambios.length + bajas.length > 0;
};

export const aPayload = (borrador) => borrador.map(({ jugadorId, clubId }) => ({ jugadorId, clubId }));

export function anadirJugador(borrador, jugador, clubId, estado) {
  if (!puedeAnadir(estado) || ids(borrador).has(jugador.jugadorId)) return borrador;
  return [...borrador, { jugadorId: jugador.jugadorId, nombre: jugador.nombre, genero: jugador.genero, clubId }];
}

export function moverJugador(borrador, jugadorId, clubId, estado, guardados) {
  if (!puedeModificar(estado, guardados, jugadorId)) return borrador;
  return borrador.map((f) => (f.jugadorId === jugadorId ? { ...f, clubId } : f));
}

export function quitarJugador(borrador, jugadorId, estado, guardados) {
  if (!puedeModificar(estado, guardados, jugadorId)) return borrador;
  return borrador.filter((f) => f.jugadorId !== jugadorId);
}

// Copia la plantilla de la temporada anterior sin pisar nada de lo ya
// editado: solo añade a quien aún no está en el borrador.
export function replicarAnterior(borrador, anteriores, estado) {
  if (!puedeReplicar(estado)) return borrador;
  const presentes = ids(borrador);
  const nuevos = anteriores
    .filter((f) => !presentes.has(f.jugadorId))
    .map((f) => ({ jugadorId: f.jugadorId, nombre: f.nombre, genero: f.genero, clubId: f.clubId }));
  return [...borrador, ...nuevos];
}

// Estado de una fila frente a lo guardado, para marcar los cambios pendientes.
export function estadoFila(guardados, fila) {
  const guardada = guardados.find((f) => f.jugadorId === fila.jugadorId);
  if (!guardada) return { tipo: 'nuevo' };
  if (guardada.clubId !== fila.clubId) return { tipo: 'movido', clubAnteriorId: guardada.clubId };
  return { tipo: 'igual' };
}

// Jugadores inscritos en algún equipo de la temporada que aún no tienen club
// en el borrador. `participantes` viene de getParticipantesReal.
export function inscritosSinClub(participantes, borrador) {
  const conClub = ids(borrador);
  const vistos = new Map();
  participantes.forEach((p) => (p.jugadores ?? []).forEach((j) => {
    const jugadorId = Number(j.jugadorId);
    if (!conClub.has(jugadorId) && !vistos.has(jugadorId)) {
      vistos.set(jugadorId, { jugadorId, nombre: j.nombre, genero: j.genero });
    }
  }));
  return [...vistos.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

// Filas guardadas que el borrador quita y que siguen en algún equipo: quitar
// a alguien del club no lo da de baja de sus equipos, y se avisa de ello.
export function bajasConEquipos(guardados, borrador) {
  return calcularCambios(guardados, borrador).bajas.filter((f) => (f.participantes ?? []).length > 0);
}

// Temporada inmediatamente anterior por id ("2025-2026" < "2026-2027"), o null.
export function temporadaAnterior(temporadas, id) {
  const anteriores = temporadas.map((t) => t.id).filter((tid) => tid < id).sort();
  return anteriores.length ? anteriores[anteriores.length - 1] : null;
}
